/* global dataLayer, RECAPTCHA_SITE_KEY_V3, FACEBOOK_APP_ID */
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import {
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
  useNavigationType,
  ScrollRestoration
} from 'react-router-dom'
import _ from 'lodash'
import fscreen from 'fscreen'
import Helmet from 'react-helmet'
import JsonLd from 'components/generic/JsonLd'
import { SITE_URL } from 'components/utils/structuredData'
import { calculateScrollPercentage } from 'scroll-percentage'
import LoadingBar from 'react-redux-loading-bar'
import { Toaster } from 'react-hot-toast'
import Rodal from 'rodal'

import mergeProps from 'reduxModules/app/dispatchers'
import stateSelector from 'reduxModules/app/selectors'

import { captureGa4Identifiers } from '../../utils/ga4Identifiers'
import { identifyUser } from '../../utils/posthog'
import { sessionExpiredOnBoot } from '../../utils/session'
import {
  getBootLocation,
  withoutSessionFlag,
  SESSION_EXPIRED_PARAM
} from '../../utils/sessionFlag'
import { getRouter } from '../../utils/navigation'
import routeAuthInfo from '../RouteGuards/routeAuth'

import logger from '../utils/logger'
import parsePathname from '../utils/parsePathname'
import useTopStackHeight from '../../hooks/useTopStackHeight'
import { fetchNewsletterDefault } from '../../utils/newsletterDefault'

// SPEC-109: the routes whose forms show the newsletter box
const NEWSLETTER_DEFAULT_ROUTES =
  /^\/(login|users\/signup|checkout|subscriptions\/plans|gift-certificate)(\/|$)/

import VerifyBanner from './VerifyBanner'
import MobileSideBar from './MobileSideBar'
import NavBar from './NavBar/index.jsx'
import Footer from './Footer'
import SubscriptionBanner from './SubscriptionBanner/index.jsx'
import SimpleFooter from './SimpleFooter'
import Confirmation from './Confirmation'
import ExternalEmailFunnel from './../ExternalEmailFunnel'

import './styles.css'
import WalkthroughBanner from './WalkthroughBanner'

// SPEC-095: the session-end flag is handled once per page load, not once
// per App mount (StrictMode, HMR and remounts would repeat the toast/event).
let sessionBootHandled = false

export function App({
  globalNotification = null,
  showBannerSubscription = true,
  ...props
}) {
  const [showingLoadingBar, setShowingLoadingBar] = useState(false)
  const routerLocation = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const navigationType = useNavigationType()

  // Create location object compatible with old location format
  const location = {
    pathname: routerLocation.pathname,
    search: routerLocation.search,
    hash: routerLocation.hash,
    state: routerLocation.state,
    // Convert searchParams to query object for backwards compatibility
    query: Object.fromEntries(searchParams.entries())
  }

  // Best-effort GA4 identifier warm-up (review S-2). On most loads GTM has not
  // injected gtag yet by the time App mounts, so this returns early without
  // latching; the real capture then happens on the first user-triggered event,
  // which also routes through captureGa4Identifiers. Kept as a cheap head start
  // for the rare case where gtag is already present.
  useEffect(() => {
    captureGa4Identifiers()
  }, [])
  // Identify the person in PostHog once authenticated (covers both fresh
  // logins and reloads with a persisted session). resetPosthog() on logout is
  // handled in navBar's logoutFlow, symmetric with the GA4 capture reset.
  useEffect(() => {
    if (props.isAuthenticated) identifyUser(props.user)
  }, [props.isAuthenticated, props.user?.id])
  // SPEC-095: decided from the URL the app booted on (a guard may have
  // already moved a login-only page to /login, whose own effect runs before
  // this one). The flag is then stripped from the current URL, keeping the
  // hash and the history state (/login's return-to lives there).
  useEffect(() => {
    if (sessionBootHandled) return
    sessionBootHandled = true
    const boot = getBootLocation()
    const { route, authRequired } = routeAuthInfo(
      boot.pathname,
      getRouter()?.routes
    )
    if (!sessionExpiredOnBoot(boot.search, { route, authRequired })) return
    const { pathname, search, hash } = window.location
    if (!new URLSearchParams(search).has(SESSION_EXPIRED_PARAM)) return
    navigate(
      { pathname, search: withoutSessionFlag(search), hash },
      { replace: true, state: window.history.state?.usr }
    )
  }, [])
  useEffect(() => {
    // Only scroll to top for PUSH navigations (new links), not POP (back/forward)
    // ScrollRestoration handles restoring position for back/forward navigation
    if (navigationType === 'PUSH') window.scrollTo(0, 0)
    props.globalPromo(location?.query?.codename)
  }, [location?.pathname, navigationType])
  useEffect(() => {
    if (props.loading === 1 && !showingLoadingBar) {
      setShowingLoadingBar(true)
      props.showLoadingBar()
    } else if (props.loading === 0 && showingLoadingBar) {
      props.hideLoadingBar()
      setShowingLoadingBar(false)
    }
  }, [props.loading, showingLoadingBar])
  // Prerender.io readiness: withhold the snapshot on each navigation, then flip
  // it back on once the page's redux fetches have settled. `props.loading` is
  // the count of in-flight requests, so loading === 0 means the primary data
  // has resolved. The grace timer both lets a route's fetch dispatch before we
  // would prematurely report ready, and lets static (no-fetch) pages settle.
  useEffect(() => {
    window.prerenderReady = false
  }, [location.pathname])
  useEffect(() => {
    if (props.loading !== 0) return undefined
    const timer = setTimeout(() => {
      window.prerenderReady = true
    }, 400)
    return () => clearTimeout(timer)
  }, [props.loading, location.pathname])
  // Screen resize listener
  useEffect(() => {
    const handleResize = () => {
      props.updateDimensions(window.innerWidth, window.innerHeight)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [props.isMobile])
  // SPEC-109: on a route that can show the newsletter box (register,
  // checkout, plans, /login, gift redemption), start the page's one
  // newsletter-default request as the route loads, so the box is already
  // ticked when a later step shows it (no visible flip). Signed-out only.
  useEffect(() => {
    if (
      !props.isAuthenticated &&
      NEWSLETTER_DEFAULT_ROUTES.test(routerLocation.pathname)
    )
      fetchNewsletterDefault()
  }, [routerLocation.pathname, props.isAuthenticated])
  // Fetch authentication data
  useEffect(() => {
    const { loadUserInfo, storeUTM, isAuthenticated } = props
    if (isAuthenticated) {
      loadUserInfo()
      dataLayer.push({ isLoggedIn: true })
    } else props.proxyLogin({ ...props, location })

    storeUTM(location.query)
    props.loadCartItems()
    props.loadCurrentPromotion()
    props.globalPromo(location?.query?.codename)
  }, [props.isAuthenticated])
  // Instantiate scrolling and full screen listeners
  useEffect(() => {
    const handleFullscreenChange = () => {
      props.updateFullscreenStatus(fscreen.fullscreenElement !== null)
    }

    const handleScroll = _.debounce((e) => {
      try {
        const childElemHeight =
          document.querySelector('.page-body').clientHeight
        const scrollPercentage = calculateScrollPercentage(
          e.target,
          childElemHeight
        )
        if (typeof scrollPercentage === 'number')
          props.updateScrollPercentage(scrollPercentage)
      } catch (error) {
        logger.error(`Error while detecting scroll height: ${error}`)
      }
    }, 250)

    props.updateDimensions(window.innerWidth, window.innerHeight)

    if (fscreen.fullscreenEnabled)
      fscreen.addEventListener(
        'fullscreenchange',
        handleFullscreenChange,
        false
      )

    window.addEventListener('scroll', handleScroll)

    return () => {
      if (fscreen.fullscreenEnabled)
        fscreen.removeEventListener('fullscreenchange', handleFullscreenChange)

      window.removeEventListener('scroll', handleScroll)
    }
  }, [])
  // Fetch Subscription Settings
  useEffect(() => {
    props.getSubscriptionPlanLanguage()
  }, [])

  const { isMobile } = props
  const userIsVerified = props.isAuthenticated && props.user.isVerified
  // the page pads by the measured height of the banners + header stack
  useTopStackHeight()
  const useSimpleFooter = () => {
    const simplifiedPaths = [
      '/customer-care',
      '/checkout',
      '/review_order',
      '/contest'
    ]
    const path = location.pathname.split('/').slice(0, 2).join('/')
    return simplifiedPaths.indexOf(path) >= 0
  }
  const isContest = () => {
    return location.pathname && location.pathname.startsWith('/contest')
  }
  const isSubscriptionPage = () => {
    return (
      location.pathname && location.pathname.startsWith('/subscriptions/plans')
    )
  }
  // SPEC-052: redesigned lead-magnet pages have their own hero + trial-block
  // CTAs; the site-wide bar/strip would stack extra asks and the floating bar
  // overlaps lesson tiles on mobile
  const isRedesignedLeadMagnetPage = () =>
    location.pathname &&
    location.pathname.startsWith('/lm/') &&
    props.isRedesignedLeadMagnet
  return (
    <div id="App">
      <ScrollRestoration />
      <Helmet>
        <title>Stan Winston School of Character Arts</title>
        <meta content="Stan Winston School" property="og:site_name" />
        <meta content={parsePathname(window.location.href)} property="og:url" />
        <meta content={FACEBOOK_APP_ID} property="fb:app_id" />
        <link
          rel="canonical"
          href={window.location.href.split('?')[0].replace(/\/$/, '')}
        />
      </Helmet>

      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          url: SITE_URL,
          name: 'Stan Winston School of Character Arts',
          potentialAction: {
            '@type': 'SearchAction',
            target: `${SITE_URL}/search?q={search_term_string}`,
            'query-input': 'required name=search_term_string'
          }
        }}
      />

      {props.isAuthenticated &&
      !userIsVerified &&
      location.pathname !== '/checkout' ? (
        <VerifyBanner displace={props.showMobileMenu && isMobile} />
      ) : null}
      {props.isAuthenticated &&
        !props?.user?.watchedWalkthroughVideo &&
        userIsVerified &&
        location.pathname !== '/checkout' && (
          <WalkthroughBanner
            displace={props.showMobileMenu && isMobile}
            isMobile={isMobile}
          />
        )}
      {isMobile ? (
        <div
          className={`mobile-menu ${
            props.showMobileMenu && isMobile ? 'displace' : ''
          }`}
        >
          <MobileSideBar pathName={location.pathname} />
        </div>
      ) : null}
      {props.showMobileMenu && isMobile && (
        <button
          type="button"
          aria-label="Close mobile sidebar by clicking outside of it"
          className="cloak"
          onClick={
            props.showMobileMenu && isMobile
              ? () => props.toggleMobileMenu()
              : null
          }
        />
      )}
      <div
        className={`page-body ${
          props.showMobileMenu && isMobile ? 'displace ' : ''
        }${props.hideNav ? 'no-nav ' : ''}${
          useSimpleFooter() ? 'enabled-simple-footer ' : ''
        }${isContest() ? 'is-contest' : ''}`}
        style={{ height: !props.hideNav ? 'auto' : props.height }}
      >
        <LoadingBar className="loading" />
        {!props.hideNav && !isContest() && (
          <NavBar pathName={location.pathname} />
        )}
        {showBannerSubscription &&
          !props.hideNav &&
          !props.isFullscreen &&
          !isContest() &&
          !isSubscriptionPage() &&
          !isRedesignedLeadMagnetPage() && (
            <SubscriptionBanner
              onClose={props.closeBannerSubscription}
              isMobile={props.isMobile}
              location={location}
            />
          )}
        <div className="content">
          <Outlet />
        </div>
        {!props.hideNav && !useSimpleFooter() && !isContest() && (
          <Footer isMobile={isMobile} />
        )}
        {!props.hideNav && useSimpleFooter() && !isContest() && (
          <SimpleFooter />
        )}
        {props.confirmation.active && (
          <Confirmation
            {...props.confirmation}
            subscription={props?.subscription}
          />
        )}
        <Toaster
          toastOptions={{
            success: {
              duration: 5000
            },
            error: {
              duration: 8000
            },
            style: {
              padding: '8px',
              maxWidth: '375px'
            },
            ariaProps: {
              role: 'status',
              'aria-live': 'polite'
            }
          }}
        />
      </div>
      <ExternalEmailFunnel {...props?.user} />
    </div>
  )
}

App.propTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
  globalNotification: PropTypes.shape({
    message: PropTypes.string.isRequired,
    level: PropTypes.string.isRequired
  }),
  showBannerSubscription: PropTypes.bool,
  showCookieNotice: PropTypes.bool.isRequired,
  confirmation: PropTypes.shape({
    active: PropTypes.bool.isRequired,
    title: PropTypes.string,
    text: PropTypes.string,
    accept: PropTypes.string,
    reject: PropTypes.string
  }).isRequired,

  // Dispatchers
  clearGlobalNotification: PropTypes.func.isRequired,
  loadUserInfo: PropTypes.func.isRequired,
  showLoadingBar: PropTypes.func.isRequired,
  hideLoadingBar: PropTypes.func.isRequired,
  closeBannerSubscription: PropTypes.func.isRequired,
  updateDimensions: PropTypes.func.isRequired,
  loadCartItems: PropTypes.func.isRequired,
  updateFullscreenStatus: PropTypes.func.isRequired,
  proxyLogin: PropTypes.func.isRequired,
  globalPromo: PropTypes.func.isRequired
}

export default connect(stateSelector, null, mergeProps)(App)
